"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";

interface TFHorizontalSliderProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  itemWidth?: number;
  gap?: number;
  autoScroll?: boolean;
  autoScrollInterval?: number;
  showControls?: boolean;
  showDots?: boolean;
  itemsPerView?: number; // Количество элементов, видимых одновременно
}

function TFHorizontalSlider<T>({
  items,
  renderItem,
  className = "",
  itemWidth = 300,
  gap = 16,
  autoScroll = false,
  autoScrollInterval = 5000,
  showControls = true,
  showDots = false,
  itemsPerView = 3,
}: TFHorizontalSliderProps<T>) {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const t = useTranslations("web.components.TFHorizontalSlider");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [maxScroll, setMaxScroll] = useState(0);
  const [calculatedItemWidth, setCalculatedItemWidth] = useState(itemWidth);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousItemWidthRef = useRef(itemWidth);
  const currentItemIndexRef = useRef(0);

  // Вычисляем текущий активный индекс для dots
  const currentIndex = Math.round(scrollPosition / (calculatedItemWidth + gap));
  // Количество позиций = количество элементов минус количество видимых + 1
  const totalPages = Math.max(1, items.length - itemsPerView + 1);

  useEffect(() => {
    const updateMaxScroll = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        // Вычисляем ширину элемента на основе контейнера и количества элементов на экране
        const newItemWidth =
          (containerWidth - gap * (itemsPerView - 1)) / itemsPerView;

        const totalWidth = items.length * (newItemWidth + gap) - gap;
        const newMaxScroll = Math.max(0, totalWidth - containerWidth);

        // Пересчитываем позицию скролла на основе сохраненного индекса элемента
        setScrollPosition((prev) => {
          if (newMaxScroll === 0) {
            currentItemIndexRef.current = 0;
            previousItemWidthRef.current = newItemWidth;
            return 0;
          }

          // Вычисляем индекс элемента на основе текущей позиции и предыдущей ширины
          const itemIndex =
            previousItemWidthRef.current > 0 && prev > 0
              ? Math.round(prev / (previousItemWidthRef.current + gap))
              : currentItemIndexRef.current;

          // Пересчитываем позицию на основе нового размера элемента
          const newPosition = itemIndex * (newItemWidth + gap);
          const correctedPosition = Math.min(
            newMaxScroll,
            Math.max(0, newPosition)
          );

          previousItemWidthRef.current = newItemWidth;
          currentItemIndexRef.current = Math.round(
            correctedPosition / (newItemWidth + gap)
          );

          return correctedPosition;
        });

        setCalculatedItemWidth(newItemWidth);
        setMaxScroll(newMaxScroll);
      }
    };

    updateMaxScroll();
    window.addEventListener("resize", updateMaxScroll);
    return () => window.removeEventListener("resize", updateMaxScroll);
  }, [items.length, gap, itemsPerView]);

  // Обновляем индекс при изменении scrollPosition
  useEffect(() => {
    if (calculatedItemWidth > 0) {
      currentItemIndexRef.current = Math.round(
        scrollPosition / (calculatedItemWidth + gap)
      );
    }
  }, [scrollPosition, calculatedItemWidth, gap]);

  useEffect(() => {
    if (!autoScroll || items.length === 0 || maxScroll <= 0) return;

    const interval = setInterval(() => {
      setScrollPosition((prev) => {
        // In RTL, scroll backwards
        const scrollAmount = calculatedItemWidth + gap;
        const next = isRTL ? prev - scrollAmount : prev + scrollAmount;

        if (isRTL) {
          if (next <= 0) {
            return maxScroll; // Reset to end
          }
        } else {
          if (next >= maxScroll) {
            return 0; // Reset to start
          }
        }
        return next;
      });
    }, autoScrollInterval);

    return () => clearInterval(interval);
  }, [
    autoScroll,
    autoScrollInterval,
    calculatedItemWidth,
    gap,
    maxScroll,
    items.length,
    isRTL,
  ]);

  const scroll = (scrollDirection: "left" | "right") => {
    if (isScrolling) return;
    setIsScrolling(true);

    const scrollAmount = calculatedItemWidth + gap;
    let newPosition: number;

    // In RTL, reverse the scroll direction logic
    if (isRTL) {
      newPosition =
        scrollDirection === "left"
          ? Math.min(maxScroll, scrollPosition + scrollAmount)
          : Math.max(0, scrollPosition - scrollAmount);
    } else {
      newPosition =
        scrollDirection === "left"
          ? Math.max(0, scrollPosition - scrollAmount)
          : Math.min(maxScroll, scrollPosition + scrollAmount);
    }

    setScrollPosition(newPosition);
    setTimeout(() => setIsScrolling(false), 300);
  };

  const scrollToPage = (pageIndex: number) => {
    if (isScrolling) return;
    setIsScrolling(true);
    const newPosition = pageIndex * (calculatedItemWidth + gap);
    const correctedPosition = Math.min(maxScroll, Math.max(0, newPosition));
    currentItemIndexRef.current = pageIndex;
    setScrollPosition(correctedPosition);
    setTimeout(() => setIsScrolling(false), 300);
  };

  if (items.length === 0) {
    return null;
  }

  // Determine button states based on RTL
  const leftButtonDisabled = isRTL
    ? scrollPosition >= maxScroll
    : scrollPosition <= 0;
  const rightButtonDisabled = isRTL
    ? scrollPosition <= 0
    : scrollPosition >= maxScroll;

  return (
    <div className={`relative ${className}`}>
      <div
        className={`flex items-center gap-4 ${isRTL ? "flex-row-reverse" : ""}`}
      >
        {showControls && (
          <button
            onClick={() => scroll("left")}
            disabled={leftButtonDisabled}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-black/10 hover:bg-black/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
            aria-label={t("scrollLeft")}
          >
            <HiChevronLeft className="w-5 h-5 text-black" />
          </button>
        )}

        <div
          ref={containerRef}
          className="flex-1 overflow-hidden"
          style={{ scrollBehavior: "smooth" }}
        >
          <motion.div
            className="flex"
            style={{
              gap: `${gap}px`,
            }}
            animate={{
              x: isRTL ? scrollPosition : -scrollPosition,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  minWidth: `${calculatedItemWidth}px`,
                  maxWidth: `${calculatedItemWidth}px`,
                }}
              >
                {renderItem(item, index)}
              </div>
            ))}
          </motion.div>
        </div>

        {showControls && (
          <button
            onClick={() => scroll("right")}
            disabled={rightButtonDisabled}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-black/10 hover:bg-black/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all"
            aria-label={t("scrollRight")}
          >
            <HiChevronRight className="w-5 h-5 text-black" />
          </button>
        )}
      </div>

      {/* Dots navigation - positioned below content */}
      {showDots && items.length > 0 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToPage(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "bg-black/30 w-8"
                  : "bg-black/10 hover:bg-black/20"
              }`}
              aria-label={t("goToPage", { page: index + 1 })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TFHorizontalSlider;
